import React, { useState } from "react";
import {
  Shield,
  Plus,
  Edit3,
  Save,
  X,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Search,
  Upload,
  MapPin,
  AlertTriangle,
  Loader2,
  Eye,
  ChevronDown,
  ChevronRight,
  Hash,
} from "lucide-react";
import { realEstateCasesApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import { motion, AnimatePresence } from "framer-motion";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Props {
  matterId: string;
  reCase: any;
  onUpdate: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const INSTRUMENT_TYPES: SelectOption[] = [
  { value: "transfer", label: "Transfer" },
  { value: "charge", label: "Charge/Mortgage" },
  { value: "discharge", label: "Discharge" },
  { value: "notice", label: "Notice" },
  { value: "other", label: "Other" },
];

const INSTRUMENT_COLORS: Record<string, string> = {
  transfer: "bg-cyan-100 text-cyan-700",
  charge: "bg-purple-100 text-purple-700",
  discharge: "bg-red-100 text-red-700",
  notice: "bg-amber-100 text-amber-700",
  other: "bg-slate-100 text-slate-600",
};

const WRIT_RESULT_OPTIONS: SelectOption[] = [
  { value: "clear", label: "Clear" },
  { value: "execution_found", label: "Execution Found" },
  { value: "pending", label: "Pending" },
];

const WRIT_RESULT_COLORS: Record<string, { bg: string; icon: React.ReactNode; label: string }> = {
  clear: { bg: "bg-emerald-100 text-emerald-700", icon: <CheckCircle2 className="h-3.5 w-3.5" />, label: "Clear" },
  execution_found: { bg: "bg-red-100 text-red-700", icon: <XCircle className="h-3.5 w-3.5" />, label: "Execution Found" },
  pending: { bg: "bg-amber-100 text-amber-700", icon: <Clock className="h-3.5 w-3.5" />, label: "Pending" },
};

const PLANNING_STATUS_OPTIONS: SelectOption[] = [
  { value: "not_applicable", label: "Not Applicable" },
  { value: "pending", label: "Pending" },
  { value: "obtained", label: "Obtained" },
];

const PLANNING_STATUS_COLORS: Record<string, { bg: string; label: string }> = {
  not_applicable: { bg: "bg-slate-100 text-slate-600", label: "Not Applicable" },
  pending: { bg: "bg-amber-100 text-amber-700", label: "Pending" },
  obtained: { bg: "bg-emerald-100 text-emerald-700", label: "Obtained" },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (d: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
    : " - ";

const formatLabel = (s: string) =>
  s?.split("_").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";

// ─── Component ──────────────────────────────────────────────────────────────

const RealEstateTitleTab: React.FC<Props> = ({ matterId, reCase, onUpdate }) => {
  // Accordion state
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    property: true,
    instruments: true,
    writs: true,
    planning: false,
    dra: false,
    insurance: true,
    evidence: false,
  });

  const toggleSection = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Global save / error state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Instrument form
  const [showInstrumentForm, setShowInstrumentForm] = useState(false);
  const [instrumentForm, setInstrumentForm] = useState({
    type: "transfer",
    instrumentNumber: "",
    registeredAt: "",
    lroOffice: "",
    notes: "",
  });

  // Writ search form
  const [showWritForm, setShowWritForm] = useState(false);
  const [writForm, setWritForm] = useState({
    partyName: "",
    searchDate: "",
    result: "pending",
    notes: "",
  });

  // Planning Act editing
  const [editingPlanning, setEditingPlanning] = useState(false);
  const [planningForm, setPlanningForm] = useState({
    required: false,
    status: "not_applicable",
    notes: "",
  });

  // DRA editing
  const [editingDra, setEditingDra] = useState(false);
  const [draForm, setDraForm] = useState({
    signed: false,
    signedDate: "",
    notes: "",
  });

  // Title insurance editing
  const [editingInsurance, setEditingInsurance] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState({
    companyName: "",
    policyNumber: "",
    ordered: false,
    orderedDate: "",
    notes: "",
  });

  if (!reCase) return null;

  const titleReg = reCase.titleRegistration || {};
  const instruments = titleReg.instruments || [];
  const writSearches = titleReg.writSearches || [];
  const planningAct = titleReg.planningActCompliance || {};
  const dra = titleReg.documentRegistrationAgreement || {};
  const titleInsurer = reCase.parties?.titleInsurer || {};
  const screenshot = titleReg.lastRegisteredInstrumentScreenshot;

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleSaveTitle = async (data: Record<string, any>) => {
    setSaving(true);
    setError(null);
    try {
      await realEstateCasesApi.updateTitle(reCase._id, data);
      onUpdate();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to save title data");
    } finally {
      setSaving(false);
    }
  };

  const handleAddInstrument = async () => {
    if (!instrumentForm.instrumentNumber.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await realEstateCasesApi.addInstrument(reCase._id, instrumentForm);
      onUpdate();
      setShowInstrumentForm(false);
      setInstrumentForm({ type: "transfer", instrumentNumber: "", registeredAt: "", lroOffice: "", notes: "" });
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to add instrument");
    } finally {
      setSaving(false);
    }
  };

  const handleAddWritSearch = async () => {
    if (!writForm.partyName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await realEstateCasesApi.addWritSearch(reCase._id, writForm);
      onUpdate();
      setShowWritForm(false);
      setWritForm({ partyName: "", searchDate: "", result: "pending", notes: "" });
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to add writ search");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlanning = async () => {
    await handleSaveTitle({ planningActCompliance: planningForm });
    setEditingPlanning(false);
  };

  const handleSaveDra = async () => {
    await handleSaveTitle({ documentRegistrationAgreement: draForm });
    setEditingDra(false);
  };

  const handleSaveInsurance = async () => {
    setSaving(true);
    setError(null);
    try {
      await realEstateCasesApi.updateParties(reCase._id, { titleInsurer: insuranceForm });
      onUpdate();
      setEditingInsurance(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to save title insurance");
    } finally {
      setSaving(false);
    }
  };

  const startEditPlanning = () => {
    setPlanningForm({
      required: planningAct.required || false,
      status: planningAct.status || "not_applicable",
      notes: planningAct.notes || "",
    });
    setEditingPlanning(true);
  };

  const startEditDra = () => {
    setDraForm({
      signed: dra.signed || false,
      signedDate: dra.signedDate ? dra.signedDate.slice(0, 10) : "",
      notes: dra.notes || "",
    });
    setEditingDra(true);
  };

  const startEditInsurance = () => {
    setInsuranceForm({
      companyName: titleInsurer.companyName || "",
      policyNumber: titleInsurer.policyNumber || "",
      ordered: titleInsurer.ordered || false,
      orderedDate: titleInsurer.orderedDate ? titleInsurer.orderedDate.slice(0, 10) : "",
      notes: titleInsurer.notes || "",
    });
    setEditingInsurance(true);
  };

  // ── Section Header helper ───────────────────────────────────────────────

  const SectionHeader = ({
    sectionKey,
    icon: Icon,
    iconBg,
    iconColor,
    title,
    badge,
    actions,
  }: {
    sectionKey: string;
    icon: React.FC<any>;
    iconBg: string;
    iconColor: string;
    title: string;
    badge?: React.ReactNode;
    actions?: React.ReactNode;
  }) => (
    <button
      onClick={() => toggleSection(sectionKey)}
      className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </div>
        <h3 className="font-semibold text-slate-800">{title}</h3>
        {badge}
      </div>
      <div className="flex items-center gap-2">
        {actions}
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform ${expanded[sectionKey] ? "rotate-180" : ""}`}
        />
      </div>
    </button>
  );

  // ── Input class ─────────────────────────────────────────────────────────

  const inputCls =
    "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {/* Error toast */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-2 text-red-700 text-sm">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </div>
            <button onClick={() => setError(null)}>
              <X className="h-4 w-4 text-red-400 hover:text-red-600" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── 1. Property Identification ──────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <SectionHeader
          sectionKey="property"
          icon={MapPin}
          iconBg="bg-cyan-50"
          iconColor="text-cyan-600"
          title="Property Identification"
        />
        {expanded.property && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-100 p-5"
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">PINs</p>
                <div className="flex flex-wrap gap-1.5">
                  {(reCase.property?.pins || []).length > 0 ? (
                    (reCase.property.pins as string[]).map((pin: string, i: number) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 bg-slate-100 rounded-lg text-sm font-mono text-slate-700"
                      >
                        {pin}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-slate-400">No PINs recorded</span>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Land Registry Office</p>
                <p className="text-sm text-slate-800">
                  {reCase.jurisdiction?.landRegistryOffice || "Not set"}
                </p>
              </div>
            </div>
            {reCase.property?.legalDescription && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-1">Legal Description</p>
                <p className="text-sm text-slate-700">{reCase.property.legalDescription}</p>
              </div>
            )}
            {reCase.property?.address && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-1">Address</p>
                <p className="text-sm text-slate-700">
                  {[
                    reCase.property.address.street,
                    reCase.property.address.unit && `Unit ${reCase.property.address.unit}`,
                    reCase.property.address.city,
                    reCase.property.address.province,
                    reCase.property.address.postalCode,
                  ]
                    .filter(Boolean)
                    .join(", ") || "Not set"}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* ─── 2. Registered Instruments ───────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <SectionHeader
          sectionKey="instruments"
          icon={FileText}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          title="Registered Instruments"
          badge={
            instruments.length > 0 ? (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {instruments.length}
              </span>
            ) : undefined
          }
        />
        {expanded.instruments && (
          <div className="border-t border-slate-100 p-5">
            {instruments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Instrument #</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Registered</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">LRO</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {instruments.map((inst: any, i: number) => (
                      <tr key={inst._id || i} className="hover:bg-slate-50">
                        <td className="py-2.5 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${INSTRUMENT_COLORS[inst.type] || INSTRUMENT_COLORS.other}`}>
                            {formatLabel(inst.type)}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-mono text-slate-700">{inst.instrumentNumber || " - "}</td>
                        <td className="py-2.5 px-3 text-slate-600">{formatDate(inst.registeredAt)}</td>
                        <td className="py-2.5 px-3 text-slate-600">{inst.lroOffice || " - "}</td>
                        <td className="py-2.5 px-3 text-slate-500 max-w-[200px] truncate">{inst.notes || " - "}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No instruments registered</p>
              </div>
            )}

            {/* Add Instrument Form */}
            <AnimatePresence>
              {showInstrumentForm && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-4 border border-slate-200 rounded-xl p-4 bg-slate-50"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Type</label>
                      <SearchableSelect
                        options={INSTRUMENT_TYPES}
                        value={instrumentForm.type}
                        onChange={(v) => setInstrumentForm((f) => ({ ...f, type: v || "transfer" }))}
                        placeholder="Select type"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Instrument #</label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="e.g. AT1234567"
                        value={instrumentForm.instrumentNumber}
                        onChange={(e) => setInstrumentForm((f) => ({ ...f, instrumentNumber: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Registered Date</label>
                      <input
                        type="date"
                        className={inputCls}
                        value={instrumentForm.registeredAt}
                        onChange={(e) => setInstrumentForm((f) => ({ ...f, registeredAt: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">LRO Office</label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="e.g. Toronto (80)"
                        value={instrumentForm.lroOffice}
                        onChange={(e) => setInstrumentForm((f) => ({ ...f, lroOffice: e.target.value }))}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="Optional notes"
                        value={instrumentForm.notes}
                        onChange={(e) => setInstrumentForm((f) => ({ ...f, notes: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={() => setShowInstrumentForm(false)}
                      className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddInstrument}
                      disabled={saving || !instrumentForm.instrumentNumber.trim()}
                      className="px-4 py-2 text-sm bg-[#0F5132] text-white rounded-xl font-medium hover:bg-[#146C43] disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      Add Instrument
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {!showInstrumentForm && (
              <button
                onClick={() => setShowInstrumentForm(true)}
                className="mt-4 flex items-center gap-1.5 text-sm text-[#0F5132] hover:text-[#146C43] font-medium"
              >
                <Plus className="h-4 w-4" />
                Add Instrument
              </button>
            )}
          </div>
        )}
      </div>

      {/* ─── 3. Writ Searches ────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <SectionHeader
          sectionKey="writs"
          icon={Search}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          title="Writ Searches"
          badge={
            writSearches.length > 0 ? (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                {writSearches.length}
              </span>
            ) : undefined
          }
        />
        {expanded.writs && (
          <div className="border-t border-slate-100 p-5">
            {writSearches.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Party Name</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Search Date</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Result</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {writSearches.map((ws: any, i: number) => {
                      const resultInfo = WRIT_RESULT_COLORS[ws.result] || WRIT_RESULT_COLORS.pending;
                      return (
                        <tr key={ws._id || i} className="hover:bg-slate-50">
                          <td className="py-2.5 px-3 font-medium text-slate-700">{ws.partyName}</td>
                          <td className="py-2.5 px-3 text-slate-600">{formatDate(ws.searchDate)}</td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${resultInfo.bg}`}>
                              {resultInfo.icon}
                              {resultInfo.label}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-slate-500 max-w-[200px] truncate">{ws.notes || " - "}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Search className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No writ searches recorded</p>
              </div>
            )}

            {/* Add Writ Search Form */}
            <AnimatePresence>
              {showWritForm && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-4 border border-slate-200 rounded-xl p-4 bg-slate-50"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Party Name</label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="Full legal name"
                        value={writForm.partyName}
                        onChange={(e) => setWritForm((f) => ({ ...f, partyName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Search Date</label>
                      <input
                        type="date"
                        className={inputCls}
                        value={writForm.searchDate}
                        onChange={(e) => setWritForm((f) => ({ ...f, searchDate: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Result</label>
                      <SearchableSelect
                        options={WRIT_RESULT_OPTIONS}
                        value={writForm.result}
                        onChange={(v) => setWritForm((f) => ({ ...f, result: v || "pending" }))}
                        placeholder="Select result"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="Optional notes"
                        value={writForm.notes}
                        onChange={(e) => setWritForm((f) => ({ ...f, notes: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={() => setShowWritForm(false)}
                      className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddWritSearch}
                      disabled={saving || !writForm.partyName.trim()}
                      className="px-4 py-2 text-sm bg-[#0F5132] text-white rounded-xl font-medium hover:bg-[#146C43] disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      Add Writ Search
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {!showWritForm && (
              <button
                onClick={() => setShowWritForm(true)}
                className="mt-4 flex items-center gap-1.5 text-sm text-[#0F5132] hover:text-[#146C43] font-medium"
              >
                <Plus className="h-4 w-4" />
                Add Writ Search
              </button>
            )}
          </div>
        )}
      </div>

      {/* ─── 4. Planning Act Compliance ──────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <SectionHeader
          sectionKey="planning"
          icon={Shield}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          title="Planning Act Compliance"
          badge={
            planningAct.status ? (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${PLANNING_STATUS_COLORS[planningAct.status]?.bg || "bg-slate-100 text-slate-600"}`}>
                {PLANNING_STATUS_COLORS[planningAct.status]?.label || formatLabel(planningAct.status)}
              </span>
            ) : undefined
          }
        />
        {expanded.planning && (
          <div className="border-t border-slate-100 p-5">
            {editingPlanning ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={planningForm.required}
                      onChange={(e) => setPlanningForm((f) => ({ ...f, required: e.target.checked }))}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Planning Act Statement Required</span>
                  </label>
                </div>
                <p className="text-xs text-slate-400 ml-7">Not required for condominiums</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Status</label>
                    <SearchableSelect
                      options={PLANNING_STATUS_OPTIONS}
                      value={planningForm.status}
                      onChange={(v) => setPlanningForm((f) => ({ ...f, status: v || "not_applicable" }))}
                      placeholder="Select status"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="Optional notes"
                      value={planningForm.notes}
                      onChange={(e) => setPlanningForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingPlanning(false)}
                    className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePlanning}
                    disabled={saving}
                    className="px-4 py-2 text-sm bg-[#0F5132] text-white rounded-xl font-medium hover:bg-[#146C43] disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Required</p>
                    <div className="flex items-center gap-1.5">
                      {planningAct.required ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-slate-400" />
                      )}
                      <span className="text-sm text-slate-700">{planningAct.required ? "Yes" : "No"}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">Not required for condominiums</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Status</p>
                    {planningAct.status ? (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PLANNING_STATUS_COLORS[planningAct.status]?.bg || "bg-slate-100 text-slate-600"}`}>
                        {PLANNING_STATUS_COLORS[planningAct.status]?.label || formatLabel(planningAct.status)}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400">Not set</span>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Notes</p>
                    <p className="text-sm text-slate-700">{planningAct.notes || " - "}</p>
                  </div>
                </div>
                <button
                  onClick={startEditPlanning}
                  className="mt-4 flex items-center gap-1.5 text-sm text-[#0F5132] hover:text-[#146C43] font-medium"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── 5. Document Registration Agreement (DRA) ────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <SectionHeader
          sectionKey="dra"
          icon={Hash}
          iconBg="bg-indigo-50"
          iconColor="text-indigo-600"
          title="Document Registration Agreement"
          badge={
            dra.signed ? (
              <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Signed
              </span>
            ) : undefined
          }
        />
        {expanded.dra && (
          <div className="border-t border-slate-100 p-5">
            {editingDra ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draForm.signed}
                      onChange={(e) => setDraForm((f) => ({ ...f, signed: e.target.checked }))}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium text-slate-700">DRA Signed</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Signed Date</label>
                    <input
                      type="date"
                      className={inputCls}
                      value={draForm.signedDate}
                      onChange={(e) => setDraForm((f) => ({ ...f, signedDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="Optional notes"
                      value={draForm.notes}
                      onChange={(e) => setDraForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingDra(false)}
                    className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveDra}
                    disabled={saving}
                    className="px-4 py-2 text-sm bg-[#0F5132] text-white rounded-xl font-medium hover:bg-[#146C43] disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Signed</p>
                    <div className="flex items-center gap-1.5">
                      {dra.signed ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-slate-400" />
                      )}
                      <span className="text-sm text-slate-700">{dra.signed ? "Yes" : "No"}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Signed Date</p>
                    <p className="text-sm text-slate-700">{formatDate(dra.signedDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Notes</p>
                    <p className="text-sm text-slate-700">{dra.notes || " - "}</p>
                  </div>
                </div>
                <button
                  onClick={startEditDra}
                  className="mt-4 flex items-center gap-1.5 text-sm text-[#0F5132] hover:text-[#146C43] font-medium"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── 6. Title Insurance ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <SectionHeader
          sectionKey="insurance"
          icon={Shield}
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
          title="Title Insurance"
          badge={
            titleInsurer.ordered ? (
              <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Ordered
              </span>
            ) : (
              <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">
                Not Ordered
              </span>
            )
          }
        />
        {expanded.insurance && (
          <div className="border-t border-slate-100 p-5">
            {editingInsurance ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Insurance Company</label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="e.g. Stewart Title, FCT, Chicago Title"
                      value={insuranceForm.companyName}
                      onChange={(e) => setInsuranceForm((f) => ({ ...f, companyName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Policy Number</label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="Policy #"
                      value={insuranceForm.policyNumber}
                      onChange={(e) => setInsuranceForm((f) => ({ ...f, policyNumber: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Ordered Date</label>
                    <input
                      type="date"
                      className={inputCls}
                      value={insuranceForm.orderedDate}
                      onChange={(e) => setInsuranceForm((f) => ({ ...f, orderedDate: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={insuranceForm.ordered}
                        onChange={(e) => setInsuranceForm((f) => ({ ...f, ordered: e.target.checked }))}
                        className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm font-medium text-slate-700">Ordered</span>
                    </label>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="Optional notes"
                      value={insuranceForm.notes}
                      onChange={(e) => setInsuranceForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingInsurance(false)}
                    className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveInsurance}
                    disabled={saving}
                    className="px-4 py-2 text-sm bg-[#0F5132] text-white rounded-xl font-medium hover:bg-[#146C43] disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Company</p>
                    <p className="text-sm font-medium text-slate-800">{titleInsurer.companyName || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Policy Number</p>
                    <p className="text-sm font-mono text-slate-700">{titleInsurer.policyNumber || " - "}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Status</p>
                    {titleInsurer.ordered ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                        <CheckCircle2 className="h-3 w-3" />
                        Ordered
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">
                        <Clock className="h-3 w-3" />
                        Not Ordered
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Ordered Date</p>
                    <p className="text-sm text-slate-700">{formatDate(titleInsurer.orderedDate)}</p>
                  </div>
                </div>
                {titleInsurer.notes && (
                  <div className="mt-3">
                    <p className="text-xs text-slate-500 mb-1">Notes</p>
                    <p className="text-sm text-slate-700">{titleInsurer.notes}</p>
                  </div>
                )}
                <button
                  onClick={startEditInsurance}
                  className="mt-4 flex items-center gap-1.5 text-sm text-[#0F5132] hover:text-[#146C43] font-medium"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── 7. Evidence Capture ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <SectionHeader
          sectionKey="evidence"
          icon={Eye}
          iconBg="bg-rose-50"
          iconColor="text-rose-600"
          title="Evidence Capture"
          badge={
            screenshot ? (
              <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Uploaded
              </span>
            ) : undefined
          }
        />
        {expanded.evidence && (
          <div className="border-t border-slate-100 p-5">
            <p className="text-xs font-medium text-slate-600 mb-3">Last Registered Instrument Screenshot</p>
            {screenshot ? (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {screenshot.fileName || "Screenshot"}
                  </p>
                  <p className="text-xs text-slate-400">
                    {screenshot.uploadedAt ? formatDate(screenshot.uploadedAt) : "Uploaded"}
                  </p>
                </div>
                <a
                  href={screenshot.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-xs text-[#0F5132] hover:text-[#146C43] font-medium rounded-lg hover:bg-emerald-50 transition-colors"
                >
                  View
                </a>
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-cyan-300 transition-colors cursor-pointer">
                <Upload className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Upload last registered instrument screenshot</p>
                <p className="text-xs text-slate-400 mt-1">PNG, JPG, or PDF</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RealEstateTitleTab;
